"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAuthClient = exports.AuthClient = void 0;
const errorCode = (error) => error instanceof Error ? error.code : undefined;
/** Access tokens stay in instance memory. Refresh credentials are HttpOnly cookies. */
class AuthClient {
    constructor(baseUrl, fetcher = (...args) => fetch(...args)) {
        this.baseUrl = baseUrl;
        this.fetcher = fetcher;
        this.accessToken = null;
        this.expiresAt = 0;
        this.refreshing = null;
        this.pendingReset = null;
        // Bound public methods preserve the existing callback/destructuring API.
        this.getToken = async () => {
            if (!this.accessToken || Date.now() >= this.expiresAt)
                await this.refresh();
            if (!this.accessToken)
                throw new Error("Sign in to continue.");
            return this.accessToken;
        };
        this.autoLogIn = async () => {
            const user = await this.refresh();
            return { userName: user.email, email: user.email, emailVerified: true, id_parent: user.id };
        };
        this.signIn = async (email, password) => {
            this.clearAccessToken();
            try {
                const result = await this.request('login', { email, password });
                this.save(result);
                return this.asCognito(result.user);
            }
            catch (error) {
                if (errorCode(error) === 'PASSWORD_RESET_REQUIRED')
                    return { challengeName: 'PASSWORD_RESET_REQUIRED', username: email };
                throw error;
            }
        };
        this.signOut = async () => {
            try {
                await this.request('logout');
            }
            finally {
                this.clearAccessToken();
            }
        };
        this.forgotPassword = async (email) => {
            const result = await this.request('request-code', { email, purpose: 'reset' });
            this.pendingReset = null;
            return result;
        };
        this.verifyResetCode = async (email, code) => {
            const result = await this.request('verify-code', { email, code, purpose: 'reset' });
            return result.resetToken;
        };
        this.completePasswordReset = async (email, resetToken, password) => {
            await this.request('reset-password', { email, purpose: 'reset', resetToken, password });
            this.pendingReset = null;
            this.clearAccessToken();
        };
        this.forgotPasswordSubmit = (email, code, password) => this.complete(email, code, password, 'reset');
        this.signUp = async (email, password, brand) => {
            await this.request('signup', { email, password, brand });
            return { userName: email, email };
        };
        this.verification = (email, code) => this.complete(email, code, undefined, 'activate');
        this.resend_verification = (email) => this.request('request-code', { email, purpose: 'activate' });
        this.setAccountStatus = async (userId, status) => this.request('account-status', { userId, status }, await this.getToken());
        this.changePassword = async (currentPassword, password) => {
            await this.request('change-password', { currentPassword, password }, await this.getToken());
            this.clearAccessToken();
        };
        // Explicitly prevent a Cognito session from bypassing the selected authority.
        this.signInGoogle = async () => {
            throw new Error('Google sign-in is not yet enabled for database authentication.');
        };
        this.newPassword = async () => {
            throw new Error('Please use the email verification code to reset your password.');
        };
    }
    async request(action, body = {}, token) {
        const { fetcher } = this;
        const response = await fetcher(`${this.baseUrl.replace(/\/$/, '')}/${action}`, {
            method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(body)
        });
        const result = await response.json();
        if (!result || typeof result !== 'object' || Array.isArray(result))
            throw new Error('Invalid authentication response.');
        const error = result;
        if (!response.ok)
            throw Object.assign(new Error(typeof error.message === 'string' ? error.message : 'Authentication failed.'), { code: typeof error.code === 'string' ? error.code : undefined });
        return result;
    }
    clearAccessToken() {
        this.accessToken = null;
        this.expiresAt = 0;
    }
    save(result) {
        this.accessToken = result.accessToken;
        this.expiresAt = Date.now() + 9 * 60 * 1000;
        return result.user;
    }
    refresh() {
        if (!this.refreshing)
            this.refreshing = this.request('refresh').then(result => this.save(result)).catch(error => {
                this.clearAccessToken();
                throw error;
            }).finally(() => { this.refreshing = null; });
        return this.refreshing;
    }
    asCognito(user) {
        return { username: user.email, attributes: { email: user.email, email_verified: true, 'custom:id_parent': user.id } };
    }
    async complete(email, code, password, purpose) {
        // Legacy combined forms must not consume another OTP when saving needs a retry.
        const key = `${purpose}:${email.trim().toLowerCase()}:${code}`;
        if (!this.pendingReset || this.pendingReset.key !== key || this.pendingReset.expiresAt <= Date.now()) {
            const { resetToken } = await this.request('verify-code', { email, code, purpose });
            this.pendingReset = { key, resetToken, expiresAt: Date.now() + 5 * 60 * 1000 };
        }
        try {
            await this.request('reset-password', { email, purpose, resetToken: this.pendingReset.resetToken, password });
            this.pendingReset = null;
            this.clearAccessToken();
        }
        catch (error) {
            if (errorCode(error) === 'INVALID_CODE')
                this.pendingReset = null;
            throw error;
        }
    }
}
exports.AuthClient = AuthClient;
function createAuthClient(baseUrl, fetcher) {
    return new AuthClient(baseUrl, fetcher);
}
exports.createAuthClient = createAuthClient;
